import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Query } from '@tanstack/react-query'

import { labelForSkyQuery } from './queryLabels'

/**
 * What the Sky dashboards are loading, right now, as one number and a few
 * lines of prose.
 *
 * ─── WHY THIS WATCHES THE CACHE INSTEAD OF THE HOOKS ────────────────────────
 * A product dashboard fetches through eleven independent hooks, each rendering
 * the moment its own data lands. That progressive behaviour is right and stays.
 * What it costs is legibility: the page takes a couple of seconds to settle and
 * says nothing while it does, even though the requests underneath are finishing
 * quickly. The fix is to narrate, not to serialise.
 *
 * Narrating from the hooks would mean instrumenting all eleven and keeping the
 * instrumentation in step forever. Every one of them keys through `skyQueryKey`
 * into the single `QueryClient` created in App.tsx, so ONE subscription to that
 * cache sees every fetch start and settle — including the ones a future hook
 * adds, which is the property that makes this maintainable.
 *
 * ─── THE CYCLE ──────────────────────────────────────────────────────────────
 *   in-flight  0 ──► 3 ──► 5 ──► 4 ──► 0
 *              │           ▲            │
 *        cycle starts  denominator  cycle ends: hold at 100%, then fade
 *                      grew (wave 2)
 *
 * The denominator is DISCOVERED, not declared: it is the number of distinct
 * tracked queries seen fetching in the current cycle. It only grows within a
 * cycle and resets at the next one.
 *
 * ⚠ It has to be discovered, because the requests arrive in waves. The filter
 * summary and record metadata fire on mount; the chart batches only key once
 * `labelQuery` has settled, which needs the product card. `useSkyCharts` also
 * groups eight chart definitions into a VARIABLE number of batched POSTs, the
 * admin page adds a campaign-tree request the departments do not have, and the
 * Comments section fetches nothing until expanded. A declared per-page manifest
 * would give a perfectly smooth bar and a second thing to keep in step — add a
 * widget, forget the manifest, and the bar sticks at 90%. A progress indicator
 * that lies is worse than one that briefly dips.
 *
 * The accepted cost: the percentage can stall or dip for a few hundred
 * milliseconds when wave 2 arrives and the denominator grows. The scrolling log
 * carries the sense of motion through it.
 */
export interface SkyLoadProgress {
  /** Whether the overlay should be on screen right now. */
  visible: boolean
  /** Settled queries in this cycle. An ERRORED query counts as settled. */
  done: number
  /** Distinct queries seen fetching in this cycle. Grows within a cycle. */
  total: number
  /** Most recent first, at most `MAX_LINES`. */
  lines: string[]
}

export interface UseSkyLoadProgressOptions {
  /**
   * How long in-flight must stay at zero before a cycle is declared over.
   *
   * 🔴 WITHOUT THIS THE OVERLAY VANISHES MID-LOAD. The page fetches in WAVES:
   * the filter summary and record metadata go first, and the chart batches only
   * key once `labelQuery` settles, which needs the product card that wave one is
   * still fetching. So in-flight legitimately touches ZERO in the gap between
   * the waves — for a render tick or two, with most of the page still to load.
   *
   * Ending the cycle there hid the overlay while the dashboard was visibly
   * unfinished, and then wave two — now counted as a "refetch" — sat behind the
   * grace period before flashing it back up. Reported by Kai on 2026-08-24:
   * "the loading screen seemed to have disappeared before all the data is
   * loaded". A short quiet period stitches the waves into one cycle.
   */
  settleMs?: number
  /** Delay before showing on a non-cold-start cycle. */
  graceMs?: number
  /** How long to hold a complete bar before fading. */
  holdMs?: number
  /** Dismiss a cycle that runs this long, so nothing can wedge the page. */
  maxCycleMs?: number
  /**
   * Which queries the overlay waits for. Default: every tracked Sky query.
   *
   * The Sky pages pass the charts and the key metrics only (speed review,
   * 16 Oct 2026): the overlay used to stay up until the SLOWEST request on the
   * page had settled, so the uncoded-comments summary (1.8 s) or the platform
   * counts gated a page whose charts were already drawn. Those panels load in
   * their own place and say so; the blur is for "nothing to read yet".
   */
  isGating?: (queryKey: readonly unknown[]) => boolean
  /**
   * Show the overlay on the cold load only, never on a refetch. The Sky pages
   * keep the previous numbers on screen, dimmed, while a filter change
   * refetches (keepWhileOnlyFilterChanges), so a blur over them would hide data
   * the page is deliberately still showing.
   */
  coldLoadOnly?: boolean
}

const DEFAULT_SETTLE_MS = 350
const DEFAULT_GRACE_MS = 400
const DEFAULT_HOLD_MS = 250
const DEFAULT_MAX_CYCLE_MS = 20000
const MAX_LINES = 3
/** How often the watchdog re-reads the cache while a cycle is live. */
const WATCHDOG_MS = 250

const IDLE: SkyLoadProgress = { visible: false, done: 0, total: 0, lines: [] }

/** Mutable per-cycle bookkeeping. Kept in a ref: none of it belongs in render. */
interface CycleState {
  /** Query hashes seen fetching this cycle — the denominator. */
  seen: Set<string>
  /**
   * Query hashes that have settled this cycle — the numerator. DERIVED from
   * `seen` minus what is fetching, recomputed every tick; never accumulated.
   */
  done: Set<string>
  /** Hashes already narrated as finished, so the log does not repeat itself. */
  logged: Set<string>
  /** Newest-first log lines. */
  lines: string[]
  /**
   * True once `maxCycleMs` has fired for this cycle. A stuck cycle must not
   * come back when the next straggler joins it — that would flash the overlay
   * on and off around a request that is never going to finish.
   */
  dismissed: boolean
}

function freshCycle(): CycleState {
  return { seen: new Set(), done: new Set(), logged: new Set(), lines: [], dismissed: false }
}

export function useSkyLoadProgress(options: UseSkyLoadProgressOptions = {}): SkyLoadProgress {
  const {
    settleMs = DEFAULT_SETTLE_MS,
    graceMs = DEFAULT_GRACE_MS,
    holdMs = DEFAULT_HOLD_MS,
    maxCycleMs = DEFAULT_MAX_CYCLE_MS,
    isGating,
    coldLoadOnly = false,
  } = options

  const client = useQueryClient()
  const [progress, setProgress] = useState<SkyLoadProgress>(IDLE)

  // ⚠ READ BY THE SUBSCRIPTION, NEVER REACTED TO. The cache subscription is set
  // up once and must survive an options change without tearing down — so the
  // timings travel through a ref rather than the effect's dependency array.
  // This is the CLAUDE.md rule 5 distinction: the dep array declares what should
  // RE-RUN the effect, not everything the effect reads.
  const timings = useRef({ settleMs, graceMs, holdMs, maxCycleMs, isGating, coldLoadOnly })
  timings.current = { settleMs, graceMs, holdMs, maxCycleMs, isGating, coldLoadOnly }

  const cycle = useRef<CycleState>(freshCycle())
  /** Query hashes that were fetching at the previous event. */
  const fetching = useRef<Set<string>>(new Set())
  /**
   * False until the first cycle has ENDED. The first cycle after mount is the
   * page's cold load and shows immediately; a department switch remounts this
   * component (the route maps each slug to its own component type), so it gets
   * a cold start too, which is the behaviour we want and costs no code.
   */
  const warmedUp = useRef(false)
  /** Whether the overlay is currently up — read inside timers, hence a ref. */
  const shown = useRef(false)

  /**
   * True from the moment a cycle starts until the quiet period confirms it is
   * over. A gap between waves does NOT end a cycle — see `settleMs`.
   */
  const cycleActive = useRef(false)

  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const graceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const maxTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback((ref: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
    if (ref.current !== null) {
      clearTimeout(ref.current)
      ref.current = null
    }
  }, [])

  useEffect(() => {
    const cache = client.getQueryCache()

    // 🔴 PUBLISH ONLY ON CHANGE. react-query fires several cache events per
    // query (fetch, success, observer add/remove, gc), so an unconditional
    // setState here re-rendered the whole dashboard tree dozens of times during
    // a load — every chart, every stat tile — and made the page MEASURABLY
    // slower than before the overlay existed. Reported by Kai, 2026-08-24:
    // "the pages do seem to be taking quite a long time to load".
    //
    // Most of those events change nothing this hook exposes. Comparing first
    // costs four scalar comparisons and saves a full render.
    const publish = () => {
      const c = cycle.current
      const lines = c.lines.slice(0, MAX_LINES)
      setProgress((prev) => {
        if (
          prev.visible === shown.current &&
          prev.done === c.done.size &&
          prev.total === c.seen.size &&
          prev.lines.length === lines.length &&
          prev.lines.every((line, i) => line === lines[i])
        ) {
          // Same object: React bails out of the re-render entirely.
          return prev
        }
        return { visible: shown.current, done: c.done.size, total: c.seen.size, lines }
      })
    }

    const show = () => {
      if (cycle.current.dismissed) return
      // A refetch on a page that keeps its previous numbers on screen.
      if (timings.current.coldLoadOnly && warmedUp.current) return
      shown.current = true
      publish()
    }

    const hide = () => {
      shown.current = false
      publish()
    }

    /**
     * Currently-fetching TRACKED queries THE PAGE IS STILL WAITING FOR.
     *
     * 🔴 `getObserversCount() > 0` IS LOAD-BEARING, NOT A TIDY-UP.
     * When a filter changes, every dependent query key changes with it, and the
     * queries under the OLD key are abandoned mid-flight: their observers move
     * to the new key, but the old query object stays in the cache with
     * `fetchStatus: 'fetching'` until it is garbage-collected — potentially for
     * minutes. Counting those left the overlay parked at "16 of 17" for ever
     * over a page that had visibly finished (seen live on 2026-08-24 at
     * /sky-social-media/all?source=apify, where seeding the source from the URL
     * re-keys every query one render in).
     *
     * A query nobody is observing is not something the page is waiting for, so
     * it is not part of this progress. That is the honest definition, and it is
     * also the one that cannot wedge.
     */
    const currentlyFetching = (): Map<string, Query> => {
      const out = new Map<string, Query>()
      for (const query of cache.getAll()) {
        if (query.state.fetchStatus !== 'fetching') continue
        if (query.getObserversCount() === 0) continue
        if (labelForSkyQuery(query.queryKey) === null) continue
        const gate = timings.current.isGating
        if (gate && !gate(query.queryKey)) continue
        out.set(query.queryHash, query)
      }
      return out
    }

    /**
     * Drop abandoned queries out of the DENOMINATOR too.
     *
     * Without this an abandoned query would silently count as "done" — the
     * bar would still reach 100%, but over a total that includes work nobody
     * ever waited for. Pruning keeps `total` describing this page's actual
     * load rather than its history.
     */
    const pruneAbandoned = (seen: Set<string>) => {
      for (const hash of [...seen]) {
        const query = cache.get(hash)
        if (!query) {
          seen.delete(hash)
          continue
        }
        if (query.state.fetchStatus === 'fetching' && query.getObserversCount() === 0) {
          seen.delete(hash)
        }
      }
    }

    const log = (line: string) => {
      // Two panels can legitimately produce the same sentence back to back
      // (the uncoded disclosure is read twice, for the count and the modal).
      // Repeating it reads as a stutter rather than as progress.
      if (cycle.current.lines[0] === line) return
      cycle.current.lines.unshift(line)
      if (cycle.current.lines.length > MAX_LINES) {
        cycle.current.lines.length = MAX_LINES
      }
    }

    const onCacheEvent = () => {
      const current = currentlyFetching()
      const previous = fetching.current

      // ── In-flight went from nothing to something ───────────────────────────
      //
      // ⚠ THIS IS NOT ALWAYS A NEW CYCLE. If the quiet period is still running
      // this is wave two of the SAME load — the chart batches arriving after
      // `labelQuery` settled — so the cycle continues: same denominator, same
      // log, overlay still up, no second grace period. See `settleMs`.
      if (previous.size === 0 && current.size > 0 && cycleActive.current) {
        clearTimer(settleTimer)
        // Wave two of the same cycle. If the overlay is not currently up —
        // because this cycle was warm and the first wave beat the grace period
        // — re-arm it, so a SLOW second wave is still narrated.
        if (!shown.current && !cycle.current.dismissed && graceTimer.current === null) {
          graceTimer.current = setTimeout(() => {
            graceTimer.current = null
            show()
          }, timings.current.graceMs)
        }
      }

      // ── Cycle start: a genuinely new burst ─────────────────────────────────
      if (previous.size === 0 && current.size > 0 && !cycleActive.current) {
        clearTimer(holdTimer)
        clearTimer(settleTimer)
        cycle.current = freshCycle()
        cycleActive.current = true

        if (warmedUp.current) {
          // A refetch. Only worth an overlay if it is actually slow — a fast
          // filter change must flash nothing.
          clearTimer(graceTimer)
          graceTimer.current = setTimeout(() => {
            graceTimer.current = null
            show()
          }, timings.current.graceMs)
        } else {
          show()
        }

        clearTimer(maxTimer)
        maxTimer.current = setTimeout(() => {
          maxTimer.current = null
          // 🔴 A cycle that never ends must never hold the page. Whatever is
          // wedged, the panels underneath still render progressively.
          cycle.current.dismissed = true
          clearTimer(graceTimer)
          hide()
        }, timings.current.maxCycleMs)
      }

      // ── Started this tick ──────────────────────────────────────────────────
      for (const [hash, query] of current) {
        if (previous.has(hash) || cycle.current.seen.has(hash)) continue
        cycle.current.seen.add(hash)
        const label = labelForSkyQuery(query.queryKey)
        if (label) log(`Loading ${label}…`)
      }

      // ── Settled this tick: LOG ONLY ────────────────────────────────────────
      // The prose is a diff of two ticks, so it can legitimately miss an event.
      // The COUNTS below are not — see the derivation.
      for (const hash of previous) {
        if (current.has(hash)) continue
        if (cycle.current.logged.has(hash)) continue
        cycle.current.logged.add(hash)
        const query = cache.get(hash)
        const label = query ? labelForSkyQuery(query.queryKey) : null
        if (!label) continue
        // An ERROR settles the same way a success does. An overlay that can
        // hang on a failure is worse than no overlay; the per-widget Alert is
        // what actually reports the problem.
        log(query?.state.status === 'error' ? `${label} failed` : `${label} loaded`)
      }

      pruneAbandoned(cycle.current.seen)

      // 🔴 `done` IS DERIVED, NEVER ACCUMULATED: everything seen this cycle
      // that is not fetching right now has, by definition, finished.
      //
      // It used to be an incremental set, added to when a query left the
      // fetching set between two ticks — and that could not be made reliable.
      // A query removed from the cache mid-flight (a key change cancels it and
      // it is garbage-collected with no observers) simply stops appearing, and
      // any tick that did not observe the transition lost the increment
      // permanently. The overlay then sat at "16 of 17" forever over a page
      // that had finished loading and an idle network — seen live on
      // 2026-08-24 with `?source=apify`.
      //
      // Derivation cannot drift: it re-answers the question from the cache
      // every tick instead of trusting a running total.
      cycle.current.done = new Set(
        [...cycle.current.seen].filter((hash) => !current.has(hash)),
      )

      fetching.current = new Set(current.keys())

      // ── Nothing in flight: the cycle MIGHT be over ─────────────────────────
      //
      // Deliberately not ended here. A wave boundary looks identical to a
      // finished load at this instant, and only time tells them apart, so the
      // decision waits out the quiet period.
      if (previous.size > 0 && current.size === 0 && cycleActive.current) {
        // 🔴 THE GRACE TIMER DIES IMMEDIATELY, not after the quiet period.
        // It exists to reveal a load that turned out to be slow; nothing is
        // loading right now, so letting it fire would flash the overlay up
        // over a page that had already finished.
        clearTimer(graceTimer)
        clearTimer(settleTimer)
        settleTimer.current = setTimeout(() => {
          settleTimer.current = null
          cycleActive.current = false
          warmedUp.current = true
          clearTimer(maxTimer)
          if (shown.current) {
            // Hold at 100% so the completed bar is actually seen rather than
            // vanishing the instant it fills.
            clearTimer(holdTimer)
            holdTimer.current = setTimeout(() => {
              holdTimer.current = null
              hide()
            }, timings.current.holdMs)
          }
        }, timings.current.settleMs)
      }

      publish()
    }

    const unsubscribe = cache.subscribe(onCacheEvent)

    // 🔴 A WATCHDOG, BECAUSE "NO EVENT ARRIVED" MUST NOT MEAN "STILL LOADING".
    // Everything above is driven by cache events, so a state the cache reaches
    // WITHOUT emitting one — a query cancelled and garbage-collected as its key
    // changes, most obviously — would leave the last computed state on screen
    // for ever. That is exactly what was seen on 2026-08-24: the overlay parked
    // at "16 of 17" over a finished page and an idle network, and even the
    // 20-second dismissal never came, because its bookkeeping was waiting on an
    // event too.
    //
    // Re-running the same handler on a slow interval costs one cheap cache scan
    // and removes the entire class. It runs only while something is actually
    // happening, so an idle page pays nothing.
    const watchdog = setInterval(() => {
      if (cycleActive.current || shown.current) onCacheEvent()
    }, WATCHDOG_MS)

    // Catch anything already in flight when this mounted.
    onCacheEvent()

    return () => {
      unsubscribe()
      clearInterval(watchdog)
      clearTimer(settleTimer)
      clearTimer(graceTimer)
      clearTimer(holdTimer)
      clearTimer(maxTimer)
    }
    // Subscribes once per client. The timings ride a ref (see above) so a
    // changed option cannot tear down and rebuild the subscription mid-load.
  }, [client, clearTimer])

  return progress
}

export default useSkyLoadProgress
